import { Alert, Modal, RadioButtons } from "components/common"
import { useAddReviewLeadMutation, useLazyGetClientConcernsQuery, useLazyGetNewTasksQuery } from "features/leads/api"
import { useGetLeadCategoriesQuery } from "features/clients/detail/api"
import { Form } from "antd"
import { getAddReviewInitialValues } from "./addReviewInitialValues"
import getAddReviewSchema from "./addReviewSchema"
import { schemaRules } from "features/shared/utils"
import { useSelector } from "react-redux"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, CONTENT_TYPES } from "utils/gtm/gtmEventsData"
import { useTranslation } from "react-i18next"
import getPrivilege from "utils/privileges/privileges"
import { useEffect, useMemo } from "react"
import { REVIEW_TYPE_CONFIG } from "./utils"

export default function AddReviewLead({ lead, client, formRef, from = {}, clientLeads }) {
  const { t } = useTranslation()
  const user = useSelector(state => state.app.user.info)
  const [form] = Form.useForm()
  const selectedLeadId = Form.useWatch("leadId")
  const [getTasks, { data, isFetching }] = useLazyGetNewTasksQuery()
  const [reviewLead, { isLoading, isError, error }] = useAddReviewLeadMutation()
  const [getClientConcerns, { data: clientConcerns, isFetching: isFetchingclientConcerns }] =
    useLazyGetClientConcernsQuery()
  const { data: leadCategoriesData } = useGetLeadCategoriesQuery({ flagVisibility: true })

  const auditReviewFlagId = leadCategoriesData?.data?.find(item => item?.key === "flagged_in_audit_review")?.id
  const isFlaggedInAuditReview = lead?.flagIds?.length ? lead?.flagIds?.includes(auditReviewFlagId) : false

  const rules = schemaRules(getAddReviewSchema())
  const userPrivileges = useSelector(state => state.app.privileges)

  const onModalVisible = () => {
    getTasks()
  }

  const onSubmit = async values => {
    const response = await reviewLead({
      user,
      lead,
      isFlaggedInAuditReview,
      ...values,
      feedbackType: allowedTypes.length === 1 ? allowedTypes[0].key : values?.reviewType,
    })
      .unwrap()
      .catch(e => {
        fireEvent({
          ...ACTION_TYPES.lead_review_submit,
          ...from,
          response: e.status,
          client_id: client.id,
          lead_id: lead.id || selectedLeadId,
          content_type: CONTENT_TYPES.lead,
        })
      })
    if (response) {
      formRef?.current?.setVisible(false)
      fireEvent({
        ...ACTION_TYPES.lead_review_submit,
        ...from,
        response: "200",
        client_id: client.id,
        lead_id: lead.id || selectedLeadId,
        content_type: CONTENT_TYPES.lead,
      })
    }
  }

  const { allowedTypes, componentToRender } = useMemo(() => {
    return REVIEW_TYPE_CONFIG().reduce(
      (acc, { key, label, value, privilege, component }) => {
        if (getPrivilege(privilege, userPrivileges)) {
          acc.allowedTypes.push({ key, label, value })
          acc.componentToRender[key] = component
        }
        return acc
      },
      { allowedTypes: [], componentToRender: {} }
    )
  }, [lead?.privileges])

  useEffect(() => {
    if (allowedTypes.length === 1 && allowedTypes?.[0]?.key === "audit_review") {
      getClientConcerns(true)
    }
  }, [allowedTypes])

  return (
    <Modal
      title={t("Review")}
      ref={formRef}
      width={670}
      okText={t("Submit")}
      cancelText={t("Cancel")}
      onCancel={() => formRef?.current?.setVisible(false)}
      onOk={form.submit}
      loading={isLoading}
      onModalVisible={onModalVisible}
      footerAlert={error}
      destroyOnClose
    >
      <Form
        form={form}
        name="add-review-lead"
        layout="vertical"
        initialValues={getAddReviewInitialValues()}
        onFinish={onSubmit}
        preserve={false}
      >
        {allowedTypes?.length > 1 && (
          <Form.Item name="reviewType" label={t("Review Type")} required rules={rules}>
            <RadioButtons
              options={allowedTypes}
              onChange={e => {
                if (e?.target?.value === "audit_review") {
                  getClientConcerns(true)
                }
              }}
            />
          </Form.Item>
        )}

        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const selectedKey = allowedTypes.length === 1 ? allowedTypes[0].key : getFieldValue("reviewType")
            const FormItemComponent = componentToRender[selectedKey]
            return FormItemComponent ? (
              <FormItemComponent
                form={form}
                rules={rules}
                data={data}
                isFetching={isFetching}
                clientLeads={clientLeads}
                lead={lead}
                concerns={clientConcerns}
                isFetchingConcerns={isFetchingclientConcerns}
                isFlaggedInAuditReview={isFlaggedInAuditReview}
              />
            ) : null
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}
